import { useMutation } from '@apollo/client';
import type { DeleteDocumentFilesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteDocumentFilesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { DeleteDocumentFilesTRPCInput } from './useDeleteDocumentFilesTRPC';
import { useDeleteDocumentFilesTRPC } from './useDeleteDocumentFilesTRPC';

export const useDeleteDocumentFiles = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [deleteGraphQL, graphqlState] = useMutation(
    DeleteDocumentFilesDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_file');
      },
    }
  );

  const trpcMutation = useDeleteDocumentFilesTRPC();

  const deleteDocumentFiles = async (
    input: DeleteDocumentFilesTRPCInput
  ): Promise<DeleteDocumentFilesMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteDocumentFiles(input);
    }

    const result = await deleteGraphQL({ variables: input });
    if (!result.data) {
      throw new Error('Failed to delete document files');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteDocumentFiles,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteDocumentFiles,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
